import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/app/GoogleSignInButton";
import { pushAnalyticsEvent } from "@/lib/analytics";
import SEO from "@/components/app/SEO";

export default function RegisterPage() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "customer" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Compte créé");
      pushAnalyticsEvent("account_created", { role: u.role, auth_provider: "password" }, { once: false });
      if (u.role === "broker") {
        pushAnalyticsEvent("broker_lead_submitted", { role: "broker" }, { once: false });
        nav("/courtier");
      } else nav("/espace");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'inscription");
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <SEO title="Créer un compte" description="Créez votre compte Résiliation Express en 30 secondes." canonical="/inscription" noindex />
      <div className="card-elevated p-8">
        <h1 className="text-2xl font-bold tracking-tight mb-2">Créer un compte</h1>
        <p className="text-sm text-muted-foreground mb-6">Suivez vos résiliations depuis un seul espace.</p>

        <GoogleSignInButton label="S'inscrire avec Google" testid="register-google-btn" />
        <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          <span>ou avec un email</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prénom</Label>
              <Input data-testid="register-firstname" value={form.first_name} onChange={upd("first_name")} required />
            </div>
            <div>
              <Label>Nom</Label>
              <Input data-testid="register-lastname" value={form.last_name} onChange={upd("last_name")} required />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="register-email" type="email" value={form.email} onChange={upd("email")} required />
          </div>
          <div>
            <Label>Mot de passe</Label>
            <Input data-testid="register-password" type="password" value={form.password} onChange={upd("password")} required minLength={8} />
          </div>
          <div>
            <Label>Type de compte</Label>
            <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} className="mt-2 grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer hover:bg-secondary transition-colors duration-200" data-testid="role-customer">
                <RadioGroupItem value="customer" id="c" />
                <span className="text-sm">Particulier</span>
              </label>
              <label className="flex items-center gap-2 border border-border rounded-md p-3 cursor-pointer hover:bg-secondary transition-colors duration-200" data-testid="role-broker">
                <RadioGroupItem value="broker" id="b" />
                <span className="text-sm">Courtier</span>
              </label>
            </RadioGroup>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-gradient-brand text-white border-0 hover:opacity-90" data-testid="register-submit-btn">
            {loading ? "Création..." : "Créer mon compte"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6 text-center">
          Déjà un compte ? <Link to="/connexion" className="text-primary font-semibold">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}
